import { p256 } from '@noble/curves/nist.js'
import { hkdf } from '@noble/hashes/hkdf.js'
import { hmac } from '@noble/hashes/hmac.js'
import { sha256, sha512 } from '@noble/hashes/sha2.js'
import { bytesToHex, concatBytes, hexToBytes, randomBytes, utf8ToBytes } from '@noble/hashes/utils.js'

// RFC 9382, P256-SHA256-HKDF-HMAC.
const Point = p256.Point
const ORDER = Point.Fn.ORDER
const M = Point.fromHex('02886e2f97ace46e55ba9dd7242579f2993b64e16ef3dcab95afd497333d8fa12f')
const N = Point.fromHex('03d8bbd6c639c62937b04d997f38c3770719c629d7014d49a24b4f98baa1292b49')

export const ID_CLIENT = 'iwspos/client'
export const ID_HOST = 'iwspos/host'
export const AAD = 'iwspos/v1'

const toInt = (b: Uint8Array) => BigInt('0x' + (bytesToHex(b) || '0'))
const toBytes32 = (n: bigint) => hexToBytes(n.toString(16).padStart(64, '0'))
const len8 = (n: number) => {
    const out = new Uint8Array(8)
    new DataView(out.buffer).setBigUint64(0, BigInt(n), true)
    return out
}
const field = (b: Uint8Array) => concatBytes(len8(b.length), b)

export const pinToW = (pin: string) => toInt(sha512(utf8ToBytes('iwspos/w/v1:' + pin))) % ORDER

export const randomScalar = () => {
    for (;;) {
        const n = toInt(randomBytes(48)) % ORDER
        if (n !== 0n) return n
    }
}

export interface Spake2Keys {
    ke: Uint8Array
    kcA: Uint8Array
    kcB: Uint8Array
    cA: Uint8Array
    cB: Uint8Array
    tt: Uint8Array
}

export interface Spake2Party {
    message: Uint8Array
    finish: (peer: Uint8Array) => Spake2Keys
}

const schedule = (tt: Uint8Array, aad: Uint8Array): Spake2Keys => {
    const h = sha256(tt)
    const ke = h.slice(0, 16)
    const ka = h.slice(16)
    const kc = hkdf(sha256, ka, undefined, concatBytes(utf8ToBytes('ConfirmationKeys'), aad), 32)
    const kcA = kc.slice(0, 16)
    const kcB = kc.slice(16)
    return { ke, kcA, kcB, cA: hmac(sha256, kcA, tt), cB: hmac(sha256, kcB, tt), tt }
}

const transcript = (a: Uint8Array, b: Uint8Array, pA: Uint8Array, pB: Uint8Array, k: Uint8Array, w: bigint) =>
    concatBytes(field(a), field(b), field(pA), field(pB), field(k), field(toBytes32(w)))

export interface Spake2Options {
    idA?: string
    idB?: string
    aad?: string
    scalar?: bigint
}

const opts = (o: Spake2Options) => ({
    a: utf8ToBytes(o.idA ?? ID_CLIENT),
    b: utf8ToBytes(o.idB ?? ID_HOST),
    aad: utf8ToBytes(o.aad ?? AAD),
})

const decodePeer = (peer: Uint8Array) => {
    const p = Point.fromBytes(peer)
    p.assertValidity()
    if (p.is0()) throw new Error('Bad SPAKE2 share')
    return p
}

export const spake2A = (w: bigint, o: Spake2Options = {}): Spake2Party => {
    const { a, b, aad } = opts(o)
    const x = o.scalar ?? randomScalar()
    const pA = M.multiply(w).add(Point.BASE.multiply(x)).toBytes(false)
    return {
        message: pA,
        finish: (pB) => {
            const k = decodePeer(pB).subtract(N.multiply(w)).multiply(x)
            if (k.is0()) throw new Error('Bad SPAKE2 share')
            return schedule(transcript(a, b, pA, pB, k.toBytes(false), w), aad)
        },
    }
}

export const spake2B = (w: bigint, o: Spake2Options = {}): Spake2Party => {
    const { a, b, aad } = opts(o)
    const y = o.scalar ?? randomScalar()
    const pB = N.multiply(w).add(Point.BASE.multiply(y)).toBytes(false)
    return {
        message: pB,
        finish: (pA) => {
            const k = decodePeer(pA).subtract(M.multiply(w)).multiply(y)
            if (k.is0()) throw new Error('Bad SPAKE2 share')
            return schedule(transcript(a, b, pA, pB, k.toBytes(false), w), aad)
        },
    }
}

export const sameMac = (a: Uint8Array, b: Uint8Array) => {
    if (a.length !== b.length) return false
    let diff = 0
    for (let i = 0; i < a.length; i++) diff |= a[i] ^ b[i]
    return diff === 0
}
